import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { Badge } from "react-bootstrap";
import io from "socket.io-client";
import { v4 as uuidv4 } from "uuid";
import { Store } from "../Store";
import { ReactComponent as VisibilityIcon } from "../assets/icons/visibility.svg";
import ChatHeaderSimple from "../components/ChatHeaderSimple";
import ChatMessageInput from "../components/ChatMessageInput";
import useAxios from "../hooks/useAxios";
import { fixScrollToBottom, getLocalTimeFormatted } from "../utils";

import { useMediaQuery } from "react-responsive";
import "../ChatScreen.css"; // Import the new CSS file for elegant styles

export default function ChatScreen({
  chatScreenVisable,
  setChatScreenVisable,
}) {
  const axios = useAxios();
  const { state, dispatch: ctxDispatch } = useContext(Store);
  const { userInfo, messages } = state;
  const ref = useRef(null);
  const [socket, setSocket] = useState(null);
  const [message, setMessage] = useState("");
  const isTabletOrMobile = useMediaQuery({ query: "(max-width: 1224px)" });

  useEffect(() => {
    if (chatScreenVisable) {
      readMessagesHandler();

      const messageIds = messages
        .filter((m) => !m.read && m.user.id !== userInfo._id)
        .map((m) => m.message.id);
      axios.put(`/api/messages/markasread`, {
        messageIds,
      });
    }
  }, [chatScreenVisable]);

  useEffect(() => {
    if (userInfo) {
      const domain = window.location.origin;
      const socket = io.connect(domain);
      setSocket(socket);
      return () => {
        socket.disconnect();
      };
    }
  }, [userInfo]);

  const sendMessageHandler = (e) => {
    e.preventDefault();
    if (!message) {
      return;
    }

    const mess = {
      recipient: "helpdesk",
      read: false,
      message: {
        text: message,
        date: new Date(),
        id: uuidv4(),
      },
      user: {
        id: userInfo._id,
        name: userInfo.name,
        businessOwner: userInfo.membership.businessOwner,
        businessName: userInfo.businessName,
      },
    };

    socket.emit("send_message", { type: "NEW_MESSAGE", message: mess });

    readMessagesHandler();

    ctxDispatch({ type: "ADD_MESSAGE", payload: mess });

    setMessage("");
  };

  useEffect(() => {
    if (userInfo && socket) {
      socket.on(userInfo._id + "_receive_message", (data) => {
        ctxDispatch({ type: "ADD_MESSAGE", payload: data });
      });
      socket.on(userInfo._id + "_receive_read_messages", (data) => {
        ctxDispatch({
          type: "RECIPIENT_READED_MESSAGES",
          payload: data.messages,
        });
      });
    }
  }, [socket, userInfo]);

  useEffect(() => {
    fixScrollToBottom("helpDeskAllMessagesPanel");
  }, [messages]);

  const fetchMessages = async () => {
    try {
      const { data } = await axios.get(
        `/api/messages/${userInfo._id}?hours=${7 * 24}`
      );

      if (data && data.length > 0) {
        ctxDispatch({
          type: "SET_MESSAGES",
          payload: { messages: data, userId: userInfo._id },
        });
      }
    } catch (err) {}
  };

  const onloadCallback = useCallback(
    (e) => {
      if (userInfo) {
        fetchMessages();
      }
    },
    [fetchMessages, userInfo, axios]
  );

  useEffect(() => {
    window.addEventListener("load", onloadCallback);

    return () => {
      window.removeEventListener("load", onloadCallback);
    };
  }, [onloadCallback]);

  useEffect(() => {
    if (!messages || messages.length === 0) {
      fetchMessages();
    }
  }, []);

  const clickOut = useCallback(
    (e) => {
      if (!ref.current.contains(e.target) && chatScreenVisable) {
        setChatScreenVisable(false);
      }
    },
    [chatScreenVisable]
  );

  useEffect(() => {
    window.addEventListener("mousedown", clickOut);

    return () => {
      window.removeEventListener("mousedown", clickOut);
    };
  }, [clickOut]);

  const activeEmit = () => {
    if (chatScreenVisable) {
      readMessagesHandler();
    }

    if (socket) {
      socket.emit("send_message", {
        type: "CLIENT_ACTIVE",
        clientId: userInfo._id,
        clientName: userInfo.name,
        clientOwner: userInfo.membership.businessOwner,
        clientBusinessName: userInfo.businessName,
        token: userInfo.token,
        recipient: "helpdesk",
      });
    }
  };

  const pcActive = useCallback(
    (e) => {
      activeEmit();
    },
    [chatScreenVisable, messages, socket, userInfo]
  );

  useEffect(() => {
    window.addEventListener("mouseenter", pcActive);

    return () => {
      window.removeEventListener("mouseenter", pcActive);
    };
  }, [pcActive]);

  useEffect(() => {
    activeEmit();
  }, [socket]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!document.hidden) activeEmit();
    }, 1000 * 2);

    return () => clearInterval(interval);
  }, [chatScreenVisable, messages, socket, userInfo]);

  const readMessagesHandler = () => {
    const messagesReaded = messages
      .filter((m) => m.user.id !== userInfo._id && !m.read)
      .map((m) => m.message.id);

    if (messagesReaded.length > 0) {
      socket.emit("send_message", {
        type: "READ_MESSAGES",
        messages: messagesReaded,
        recipient: "helpdesk",
      });

      ctxDispatch({
        type: "READ_MESSAGES_NOT",
        payload: { userId: userInfo._id },
      });
    }
  };

  return (
    <div
      ref={ref}
      className={`chat-icon-container ${!chatScreenVisable ? "closed" : ""}`}
    >
      {!chatScreenVisable ? (
        <div className="chat-icon">
          {messages &&
            messages?.filter((m) => !m.read && m.user.id !== userInfo._id)
              .length > 0 && (
              <div className="chat-badge">
                {messages && messages.length > 0 && (
                  <Badge pill bg="danger">
                    {
                      messages?.filter(
                        (m) => !m.read && m.user.id !== userInfo._id
                      ).length
                    }
                  </Badge>
                )}
              </div>
            )}
        </div>
      ) : (
        <div
          style={
            isTabletOrMobile
              ? { minWidth: "100%" }
              : { maxWidth: "430px", minWidth: "430px" }
          }
        >
          <ChatHeaderSimple setChatScreenVisable={setChatScreenVisable} />
          <div className="chat-container">
            <div id="helpDeskAllMessagesPanel" className="chat-scroll">
              {messages.map((m) =>
                m.user.businessName !== "HelpDesk" ? (
                  <div key={m.message.id} className="message-sent">
                    <div className="message-bubble user-message">
                      <div>{m.message.text}</div>
                      <div className="message-details">
                        {getLocalTimeFormatted(m.message.date)}&nbsp;
                        {m.user.name}
                      </div>
                    </div>
                    <div className="message-status">
                      {m.read && <VisibilityIcon width="14" fill="#2994b2" />}
                    </div>
                  </div>
                ) : (
                  <div key={m.message.id} className="message-received">
                    <div className="message-bubble">
                      <div>{m.message.text}</div>
                      <div className="message-details">
                        {getLocalTimeFormatted(m.message.date)}&nbsp;
                        {m.user.name}
                      </div>
                    </div>
                  </div>
                )
              )}
            </div>
            <ChatMessageInput
              message={message}
              setMessage={setMessage}
              sendMessageHandler={sendMessageHandler}
            />
          </div>
        </div>
      )}
    </div>
  );
}
